import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle, ChevronRight, Check } from 'lucide-react';
import { COLORS } from '../constants';
import { SCOOTERS } from '../data/scooters';
import { QUIZ_QUESTIONS } from '../data/quiz';
import Button from '../components/Button';
import { startCheckout } from "../utils/checkout";


const QuizPage = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQuestion]: value };
    setAnswers(newAnswers);

    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      calculateResult(newAnswers);
    }
  };

	const calculateResult = (finalAnswers) => {
  const scores = { "M12-1": 0, "M12-3": 0, "ESGOLF": 0 };

  // Q1 — Terrain
  if (finalAnswers[0] === "flat") scores["ESGOLF"] += 2;
  if (finalAnswers[0] === "moderate") scores["M12-1"] += 2;
  if (finalAnswers[0] === "hilly") scores["M12-3"] += 3;

  // Q2 — Holes played
  if (finalAnswers[1] === "9holes") scores["ESGOLF"] += 2;
  if (finalAnswers[1] === "18holes") scores["M12-1"] += 2;
  if (finalAnswers[1] === "36holes") scores["M12-3"] += 3;

  // Q3 — Priority
  if (finalAnswers[2] === "stability") scores["M12-1"] += 3;
  if (finalAnswers[2] === "power") scores["M12-3"] += 3;
  if (finalAnswers[2] === "balance") scores["ESGOLF"] += 3;

  // Q4 — Transport
  if (finalAnswers[3] === "portable") scores["ESGOLF"] += 3;
  if (finalAnswers[3] === "sometimes") scores["M12-1"] += 1;
  if (finalAnswers[3] === "rarely") scores["M12-3"] += 1;

  // Q5 — Accessories
  if (finalAnswers[4] === "seat") scores["M12-1"] += 2;
  if (finalAnswers[4] === "battery") scores["M12-3"] += 2;
  if (finalAnswers[4] === "full") scores["ESGOLF"] += 1;

  const winner = Object.entries(scores)
    .sort((a, b) => b[1] - a[1])[0][0];

  const recommendedScooter = SCOOTERS.find(s => s.name === winner);
  setResult(recommendedScooter);
};

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
  };

  if (result) {
    return (
      <section className="py-20 min-h-screen" style={{ backgroundColor: COLORS.lightSurface }}>
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[3rem] overflow-hidden shadow-2xl"
          >
            <div className="relative h-96">
              <img
                src={result.image}
                alt={result.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute bottom-10 left-10 right-10 text-white">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <CheckCircle size={48} className="mb-4" style={{ color: COLORS.accent }} />
                  <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4">
                    Your Perfect Match: {result.name}
                  </h2>
                  <p className="text-xl opacity-90 italic">{result.tagline}</p>
                </motion.div>
              </div>
            </div>

            <div className="p-12">
              <h3 className="text-2xl font-bold mb-6" style={{ color: COLORS.secondaryBg }}>
                Why This Model?
              </h3>
              <p className="text-lg leading-relaxed mb-8 opacity-80" style={{ color: COLORS.textPrimary }}>
                {result.longDescription.substring(0, 300)}...
              </p>

              <div className="bg-[#F9F7F2] p-8 rounded-3xl mb-8">
                <h4 className="font-bold mb-4 text-[#1E1E1C]">Key Features for You:</h4>
                <ul className="space-y-3">
                  {result.pointers.slice(0, 5).map((pointer, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check size={20} style={{ color: COLORS.accent }} />
                      <span className="text-[#1E1E1C]">{pointer}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="primary"
                  className="flex-1"
		  style={{ color: 'black'}} 
                  onClick={() => startCheckout(result)}>

                   	 Buy Now
                </Button>
                <Button variant="secondary" className="flex-1" style={{ color: 'black'}} onClick={resetQuiz}>
                  Retake Quiz
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;
  const question = QUIZ_QUESTIONS[currentQuestion];

  return (
    <section className="py-20 min-h-screen flex items-center" style={{ backgroundColor: COLORS.lightSurface }}>
      <Helmet>
        <title>Find Your Perfect Golf Scooter | Quiz | Lectric Leisure</title>
	 <link rel="canonical" href="https://lectricleisure.golf/quiz" />
        <meta
          name="description"
          content="Take our quick 5-question quiz to find the perfect electric golf scooter for your needs."
        />
      </Helmet>

      <div className="container mx-auto px-6 max-w-3xl w-full">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4" style={{ color: COLORS.secondaryBg }}>
            Find Your Perfect Scooter
          </h1>
          <p className="text-xl opacity-80" style={{ color: COLORS.textPrimary }}>
            Answer 5 quick questions to get your personalized recommendation
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex justify-between text-sm font-bold mb-2" style={{ color: COLORS.accent }}>
            <span>Question {currentQuestion + 1} of {QUIZ_QUESTIONS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-3 bg-[#E0D8C8] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="h-full rounded-full"
              style={{ backgroundColor: COLORS.accent }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-white rounded-[3rem] p-12 shadow-2xl"
          >
            <h2 className="text-3xl font-bold mb-10 text-center font-serif" style={{ color: COLORS.secondaryBg }}>
              {question.question}
            </h2>

            <div className="space-y-4">
              {question.options.map((option, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.02, x: 10 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAnswer(option.value)}
                  className="w-full p-6 rounded-2xl border-2 border-[#E0D8C8] hover:border-[#C7A36A] hover:bg-[#C7A36A]/5 transition-all text-left flex items-center gap-6 group"
                >
                  <span className="text-4xl group-hover:scale-125 transition-transform">{option.icon}</span>
                  <span className="text-xl font-bold flex-1" style={{ color: COLORS.textPrimary }}>
                    {option.text}
                  </span>
                  <ChevronRight
                    size={24}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: COLORS.accent }}
                  />
                </motion.button>
              ))}
            </div>

            {currentQuestion > 0 && (
              <button
                onClick={() => setCurrentQuestion(currentQuestion - 1)}
                className="mt-8 flex items-center gap-2 text-[#7A6F60] hover:text-[#1E1E1C] font-bold transition-colors mx-auto"
              >
                <ArrowLeft size={20} /> Previous Question
              </button>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default QuizPage;
